import { OneOfListElements } from '@shared/models/project.model';
import { GroupSectionData, SectionRowContentComponent } from './list.model';
import { RefObject, useMemo, useRef } from 'react';
import { DefaultSectionRowContent } from './DefaultSectionRowContent';
import { LIST_ITEM_HEIGHT, LIST_SPACING, Row } from '.';
import { guards } from '@shared/helpers/typeguards.helpers';
import { GroupSectionMeta } from '@features/elements/element-grouping.model';
import { useHover } from 'usehooks-ts';

type GroupSectionRowProps<Element = OneOfListElements> = {
  section: GroupSectionData<Element, GroupSectionMeta>;
  onExpandToggle?: (next: boolean) => void;
  expanded: boolean;
  children: React.ReactNode;
  SectionRowContent?: SectionRowContentComponent<Element, GroupSectionMeta>;
  onDoubleClick?: () => void;
};

export const GroupSectionRow: React.FC<GroupSectionRowProps> = ({
  section,
  onExpandToggle,
  expanded,
  children,
  SectionRowContent,
  onDoubleClick,
}) => {
  const toggleCollapsed = useMemo(
    () =>
      guards.is.defined(onExpandToggle) &&
      guards.is.nonEmptyArray(section.items)
        ? () => onExpandToggle(!expanded)
        : undefined,
    [expanded, onExpandToggle, section.items],
  );

  const RowContentComponent = SectionRowContent ?? DefaultSectionRowContent;
  const hoverRef = useRef<HTMLDivElement>(null);
  const isHovered = useHover(hoverRef as RefObject<HTMLElement>);
  return (
    <>
      <Row
        ref={hoverRef}
        padding
        spacing={LIST_SPACING.DEFAULT}
        disabled={guards.is.emptyArray(section.items)}
        height={LIST_ITEM_HEIGHT}
        onDoubleClick={onDoubleClick}
        hover={isHovered}
      >
        <RowContentComponent
          section={section}
          expanded={expanded}
          isHovered={isHovered}
          onExpandToggle={toggleCollapsed}
        />
      </Row>
      {children}
    </>
  );
};
